import { useTranslation } from 'react-i18next';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Mail, Server, TestTube, CheckCircle, AlertCircle, Loader2, Eye, EyeOff, Send } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EmailSettings = () => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    smtp_from_email: '',
    smtp_from_name: 'Project Manager',
    smtp_use_tls: true,
    smtp_use_ssl: false,
    email_notifications_enabled: false,
    notify_user_on_registration: true,
    notify_user_on_approval: true,
    notify_user_on_role_change: true,
    notify_admin_on_new_registration: true,
    admin_notification_email: ''
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testEmail, setTestEmail] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/smtp`);
      setSettings(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error('Error fetching SMTP settings:', error);
      if (error.response?.status !== 403) {
        toast.error(t('auto.set.failedToLoadEmailSettings'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/smtp`, settings);
      toast.success(t('auto.set.emailSettingsSavedSuccessfully'));
    } catch (error) {
      console.error('Error saving SMTP settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save email settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    if (!testEmail) {
      toast.error(t('auto.set.pleaseEnterATestEmailAddress'));
      return;
    }
    
    setTesting(true);
    try {
      const response = await axios.post(`${API}/settings/smtp/test`, { test_email: testEmail });
      toast.success(response.data.message);
      setTestDialogOpen(false);
      setTestEmail('');
    } catch (error) {
      console.error('SMTP test failed:', error);
      toast.error(error.response?.data?.detail || 'SMTP test failed');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-2">{t('auto.set.loadingEmailSettings')}</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Mail className="h-8 w-8 text-blue-600" />
          <div>
            <h2 className="text-2xl font-bold text-slate-900">{t('auto.set.emailSettings')}</h2>
            <p className="text-slate-600">{t('auto.set.configureSmtpServerForEmailNotifications')}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            onClick={() => setTestDialogOpen(true)}
            disabled={!settings.smtp_host || !settings.smtp_username}
          >
            <TestTube className="h-4 w-4 mr-2" />
            {t('auto.set.testConnection')}
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="btn-save">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
            Save Settings
          </Button>
        </div>
      </div>

      <Tabs defaultValue="server" className="space-y-4">
        <TabsList className="flex flex-wrap h-auto gap-1 p-1">
          <TabsTrigger value="server" className="flex items-center gap-2" data-testid="tab-server">
            <Server className="h-4 w-4" />
            {t('auto.set.smtpServer')}
          </TabsTrigger>
          <TabsTrigger value="notifications" className="flex items-center gap-2" data-testid="tab-notifications">
            <Send className="h-4 w-4" />
            {t('auto.set.notifications')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="server">
          <Card>
            <CardHeader>
              <CardTitle>{t('auto.set.smtpServerConfiguration')}</CardTitle>
              <CardDescription>
                Configure your company&apos;s SMTP server to send email notifications
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Enable/Disable Toggle */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <Label className="text-base font-medium">{t('auto.set.enableEmailNotifications')}</Label>
                  <p className="text-sm text-gray-500">{t('auto.set.turnOnToSendEmailNotifications')}</p>
                </div>
                <Switch
                  checked={settings.email_notifications_enabled}
                  onCheckedChange={(checked) => handleChange('email_notifications_enabled', checked)}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* SMTP Host */}
                <div className="space-y-2">
                  <Label htmlFor="smtp_host">{t('auto.set.smtpServerHost')}</Label>
                  <Input
                    id="smtp_host"
                    value={settings.smtp_host || ''}
                    onChange={(e) => handleChange('smtp_host', e.target.value)}
                    placeholder="smtp.example.com"
                  />
                </div>

                {/* SMTP Port */}
                <div className="space-y-2">
                  <Label htmlFor="smtp_port">{t('auto.set.smtpPort')}</Label>
                  <Input
                    id="smtp_port"
                    type="number"
                    value={settings.smtp_port}
                    onChange={(e) => handleChange('smtp_port', parseInt(e.target.value) || 587)}
                    placeholder="587"
                  />
                  <p className="text-xs text-gray-500">{t('auto.set.commonPorts587Tls465Ssl')}</p>
                </div>

                {/* SMTP Username */}
                <div className="space-y-2">
                  <Label htmlFor="smtp_username">{t('auto.set.username')}</Label>
                  <Input
                    id="smtp_username"
                    value={settings.smtp_username || ''}
                    onChange={(e) => handleChange('smtp_username', e.target.value)}
                    placeholder="your-email@example.com"
                  />
                </div>

                {/* SMTP Password */}
                <div className="space-y-2">
                  <Label htmlFor="smtp_password">{t('auto.set.password')}</Label>
                  <div className="relative">
                    <Input
                      id="smtp_password"
                      type={showPassword ? 'text' : 'password'}
                      value={settings.smtp_password || ''}
                      onChange={(e) => handleChange('smtp_password', e.target.value)}
                      placeholder={t('auto.set.enterSmtpPassword')}
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                {/* From Email */}
                <div className="space-y-2">
                  <Label htmlFor="smtp_from_email">{t('auto.set.fromEmailAddress')}</Label>
                  <Input
                    id="smtp_from_email"
                    type="email"
                    value={settings.smtp_from_email || ''}
                    onChange={(e) => handleChange('smtp_from_email', e.target.value)}
                    placeholder="noreply@example.com"
                  />
                </div>

                {/* From Name */}
                <div className="space-y-2">
                  <Label htmlFor="smtp_from_name">{t('auto.set.fromName')}</Label>
                  <Input
                    id="smtp_from_name"
                    value={settings.smtp_from_name || ''}
                    onChange={(e) => handleChange('smtp_from_name', e.target.value)}
                    placeholder={t('auto.set.projectManager')}
                  />
                </div>
              </div>

              {/* Security Options */}
              <div className="pt-4 border-t">
                <h3 className="text-lg font-medium mb-4">{t('auto.set.securitySettings')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <Label className="font-medium">{t('auto.set.useTlsStarttls')}</Label>
                      <p className="text-xs text-gray-500">{t('auto.set.recommendedForPort587')}</p>
                    </div>
                    <Switch
                      checked={settings.smtp_use_tls}
                      onCheckedChange={(checked) => {
                        handleChange('smtp_use_tls', checked);
                        if (checked) handleChange('smtp_use_ssl', false);
                      }}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <Label className="font-medium">{t('auto.set.useSsl')}</Label>
                      <p className="text-xs text-gray-500">{t('auto.set.recommendedForPort465')}</p>
                    </div>
                    <Switch
                      checked={settings.smtp_use_ssl}
                      onCheckedChange={(checked) => {
                        handleChange('smtp_use_ssl', checked);
                        if (checked) handleChange('smtp_use_tls', false);
                      }}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notifications">
          <Card>
            <CardHeader>
              <CardTitle>{t('auto.set.notificationSettings')}</CardTitle>
              <CardDescription>
                {t('auto.set.chooseWhichEventsShouldTriggerEmail')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* User Notifications */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium">{t('auto.set.userNotifications')}</h3>
                
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <Label className="font-medium">{t('auto.set.registrationConfirmation')}</Label>
                    <p className="text-sm text-gray-500">{t('auto.set.sendEmailWhenUserRegistersPending')}</p>
                  </div>
                  <Switch
                    checked={settings.notify_user_on_registration}
                    onCheckedChange={(checked) => handleChange('notify_user_on_registration', checked)}
                  />
                </div>

                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <Label className="font-medium">{t('auto.set.accountApproval')}</Label>
                    <p className="text-sm text-gray-500">{t('auto.set.notifyUserWhenTheirAccountIs')}</p>
                  </div>
                  <Switch
                    checked={settings.notify_user_on_approval}
                    onCheckedChange={(checked) => handleChange('notify_user_on_approval', checked)}
                  />
                </div>

                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <Label className="font-medium">{t('auto.set.roleChanges')}</Label>
                    <p className="text-sm text-gray-500">{t('auto.set.notifyUserWhenTheirRoleIs')}</p>
                  </div>
                  <Switch
                    checked={settings.notify_user_on_role_change}
                    onCheckedChange={(checked) => handleChange('notify_user_on_role_change', checked)}
                  />
                </div>
              </div>

              {/* Admin Notifications */}
              <div className="space-y-4 pt-4 border-t">
                <h3 className="text-lg font-medium">{t('auto.set.adminNotifications')}</h3>
                
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <Label className="font-medium">{t('auto.set.newRegistrationAlert')}</Label>
                    <p className="text-sm text-gray-500">{t('auto.set.alertAdminWhenANewUser')}</p>
                  </div>
                  <Switch
                    checked={settings.notify_admin_on_new_registration}
                    onCheckedChange={(checked) => handleChange('notify_admin_on_new_registration', checked)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="admin_email">{t('auto.set.adminNotificationEmail')}</Label>
                  <Input
                    id="admin_email"
                    type="email"
                    value={settings.admin_notification_email || ''}
                    onChange={(e) => handleChange('admin_notification_email', e.target.value)}
                    placeholder="admin@example.com"
                  />
                  <p className="text-xs text-gray-500">{t('auto.set.emailAddressToReceiveAdminNotifications')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Status Info */}
      <Card className={settings.email_notifications_enabled ? "border-green-200 bg-green-50" : "border-yellow-200 bg-yellow-50"}>
        <CardContent className="pt-6">
          <div className="flex items-center gap-3">
            {settings.email_notifications_enabled ? (
              <>
                <CheckCircle className="h-6 w-6 text-green-600" />
                <div>
                  <p className="font-medium text-green-800">{t('auto.set.emailNotificationsAreEnabled')}</p>
                  <p className="text-sm text-green-600">{t('auto.set.usersWillReceiveEmailNotificationsBased')}</p>
                </div>
              </>
            ) : (
              <>
                <AlertCircle className="h-6 w-6 text-yellow-600" />
                <div>
                  <p className="font-medium text-yellow-800">{t('auto.set.emailNotificationsAreDisabled')}</p>
                  <p className="text-sm text-yellow-600">{t('auto.set.enableEmailNotificationsToSendEmails')}</p>
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Test Connection Dialog */}
      <Dialog open={testDialogOpen} onOpenChange={setTestDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('auto.set.testSmtpConnection')}</DialogTitle>
            <DialogDescription>
              {t('auto.set.sendATestEmailToVerify')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="test_email">{t('auto.set.sendTestEmailTo')}</Label>
              <Input
                id="test_email"
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="your-email@example.com"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setTestDialogOpen(false)} data-testid="dialog-cancel-btn">
              {t('auto.set.cancel')}
            </Button>
            <Button onClick={handleTestConnection} disabled={testing || !testEmail} data-testid="dialog-testconnection-btn">
              {testing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('auto.set.sending')}
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  {t('auto.set.sendTestEmail')}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmailSettings;
